// RITA-1543 — Abfragen der Vorlagenwahl bei „Vertrag erstellen“ (Backend: getContractCreation, nur
// Super-Admins). Die Karte fragt ohne Dokumente ab (bis 512 kB je Vorlage); erst das Fenster lädt die
// Dokumente der gewählten Vorlagen und den Namen der angemeldeten Person.
// RITA-1552: Beide Abfragen nehmen `kinds` (dispatch_letter, contract, return_letter): die Karte alle drei Arten, Fenster
// und Bearbeiten-Seite nur die Arten ihres Vorgangs (services/vorgang.ts). Ohne `kinds` liefert der Server wie bisher
// Anschreiben Versand und Vertrag.
import { gql } from '@apollo/client';
import type {
  ContractCreation,
  ContractCreationKind,
  ContractCreationDocument,
  ContractTemplate,
  ContractTemplateAssignment,
} from 'global-intern-types';

/** Die Felder einer Vorlage, die Karte und Meldungen brauchen. */
export type Vorlagenkurz = Pick<ContractTemplate, 'id' | 'name'> & {
  /** RITA-1547: Stand der Vorlage, den ein Entwurf als Herkunft speichert. */
  updated_at?: string | null;
  assignment: ContractTemplateAssignment | null;
  /** Nur im Fenster abgefragt. */
  document?: Record<string, unknown> | null;
};

export type VertragsauswahlDokument = Omit<ContractCreationDocument, 'template' | 'candidates'> & {
  template: Vorlagenkurz | null;
  candidates: Vorlagenkurz[];
};

export type Vertragsauswahl = Omit<ContractCreation, 'documents' | 'editor_first_name' | 'editor_last_name'> & {
  editor_first_name?: string | null;
  editor_last_name?: string | null;
  documents: VertragsauswahlDokument[];
};

export interface VertragsauswahlAntwort {
  getContractCreation: Vertragsauswahl;
}

export interface VertragsauswahlVariablen {
  agent_id: number;
  /** RITA-1552: angefragte Arten; ohne Angabe Anschreiben Versand und Vertrag (Server). */
  kinds?: readonly ContractCreationKind[];
}

// Dieselben Zuordnungsfelder wie in features/templates/api/contractTemplates.ts, damit Apollo das
// nicht normalisierte `assignment` derselben Vorlage im Cache nicht mit weniger Feldern überschreibt.
const VORLAGE_KURZ = `
  id
  name
  updated_at
  assignment {
    kind
    branch_office_id
    branch_office_name
    contract_type_id
    contract_type_name
    gender
  }
`;

export const GET_VERTRAGSAUSWAHL = gql`
  query getVertragsauswahl($agent_id: Int!, $kinds: [String!]) {
    getContractCreation(agent_id: $agent_id, kinds: $kinds) {
      agent_id
      branch_office_name
      contract_type_name
      gender
      contract_gender
      business_owner_type_missing
      has_business_owner
      documents {
        kind
        status
        template {
          ${VORLAGE_KURZ}
        }
        candidates {
          ${VORLAGE_KURZ}
        }
      }
    }
  }
`;

export const GET_VERTRAGSAUSWAHL_MIT_DOKUMENTEN = gql`
  query getVertragsauswahlMitDokumenten($agent_id: Int!, $kinds: [String!]) {
    getContractCreation(agent_id: $agent_id, kinds: $kinds) {
      agent_id
      branch_office_name
      contract_type_name
      gender
      contract_gender
      business_owner_type_missing
      has_business_owner
      editor_first_name
      editor_last_name
      documents {
        kind
        status
        template {
          ${VORLAGE_KURZ}
          document
        }
        candidates {
          ${VORLAGE_KURZ}
        }
      }
    }
  }
`;
